import { Catalog, Delivery, Home } from "@carbon/icons-react";
import { TFunction } from "i18next";
import { ReactElement } from "react";
import { PlusCircle } from "react-feather";

import { MenuItem } from "../../models/autogenerated-deliveryService-gql/graphql";
import { Roles } from "../../models/autogenerated-deliveryService-gql/graphql";
import { RoutePath } from "../../models/routePath";
import { SidebarItemsType } from "../../models/sidebar";
import { splitRole } from "../../utils/roleNormalization";

const getDefaultSection = (t: TFunction) =>
  [
    /* {
      href: "/",
      icon: <OverflowMenuHorizontal size={24} />,
      title: t("more"),
      children: [
        {
          href: "/",
          icon: <PenFountain size={16} />,
          title: t("Placeholder"),
        },
      ],
    }, */
  ] as SidebarItemsType[];

const buildPage = (
  href: string,
  title: string,
  icon: ReactElement,
  exactMatch = false,
): SidebarItemsType => ({
  href,
  title,
  icon,
  children: [],
  exactMatch,
});

const shouldUseExactMatch = (route: RoutePath): boolean => {
  return route === RoutePath.OutboundDeliveries;
};

const resolveRoutePath = (menuRoute: string): RoutePath | undefined => {
  switch (menuRoute) {
    case "/":
    case RoutePath.Home:
      return RoutePath.Home;
    case RoutePath.InboundDeliveries:
    case "/deliveries":
      return RoutePath.InboundDeliveries;
    case RoutePath.OutboundDeliveries:
    case "/outboundDeliveries":
      return RoutePath.OutboundDeliveries;
    case RoutePath.NewOutboundDelivery:
      return RoutePath.NewOutboundDelivery;
    case RoutePath.NewOrder:
      return RoutePath.NewOrder;
    case RoutePath.Stocks:
      return RoutePath.Stocks;
    default:
      return undefined;
  }
};

const getIconByRoute = (route: string): ReactElement => {
  switch (route) {
    case RoutePath.Home:
      return <Home size={24} />;
    case RoutePath.NewOrder:
    case RoutePath.NewOutboundDelivery:
      return <PlusCircle size={24} />;
    case RoutePath.Stocks:
      return <Catalog size={24} />;
    case RoutePath.InboundDeliveries:
    case RoutePath.OutboundDeliveries:
    default:
      return <Delivery size={24} />;
  }
};

const getTitleByRoute = (
  t: TFunction,
  route: RoutePath,
  fallbackLabel: string,
): string => {
  switch (route) {
    case RoutePath.Home:
      return t("home");
    case RoutePath.InboundDeliveries:
    case RoutePath.OutboundDeliveries:
      return t("deliveries");
    case RoutePath.NewOrder:
    case RoutePath.NewOutboundDelivery:
      return t("newOrder");
    case RoutePath.Stocks:
      return t("stockOverview");
    default:
      return fallbackLabel;
  }
};

const getEffectiveActiveRole = (activeRole: Roles | null): Roles | null => {
  if (!activeRole) return null;

  return splitRole(activeRole)[0] || null;
};

const getAllowedRoutesForRole = (activeRole: Roles | null): RoutePath[] => {
  const effectiveRole = getEffectiveActiveRole(activeRole);

  switch (effectiveRole) {
    case Roles.Supplier:
      return [RoutePath.Home, RoutePath.InboundDeliveries, RoutePath.NewOrder];
    case Roles.Carrier:
      return [RoutePath.Home, RoutePath.InboundDeliveries];
    case Roles.Customer:
      return [
        RoutePath.Home,
        RoutePath.OutboundDeliveries,
        RoutePath.NewOutboundDelivery,
        RoutePath.Stocks,
      ];
    default:
      return [];
  }
};

const getDeliveriesRouteForRole = (activeRole: Roles | null): RoutePath => {
  const effectiveRole = getEffectiveActiveRole(activeRole);

  if (effectiveRole === Roles.Customer) {
    return RoutePath.OutboundDeliveries;
  }

  return RoutePath.InboundDeliveries;
};

const mapRoleSpecificRoute = (
  route: RoutePath,
  activeRole: Roles | null,
): RoutePath => {
  if (
    route === RoutePath.InboundDeliveries ||
    route === RoutePath.OutboundDeliveries
  ) {
    return getDeliveriesRouteForRole(activeRole);
  }

  return route;
};

export const mapMenuItemsToNavItems = (
  t: TFunction,
  menuItems: MenuItem[] | undefined,
  activeRole: Roles | null,
) => {
  if (!menuItems || menuItems.length === 0) {
    return [
      {
        title: "",
        pages: getDefaultSection(t),
      },
    ];
  }

  const allowedRoutes = getAllowedRoutesForRole(activeRole);

  const pages = menuItems
    .map((menuItem) => {
      const resolvedRoute = resolveRoutePath(menuItem.route);

      if (!resolvedRoute) return undefined;

      const route = mapRoleSpecificRoute(resolvedRoute, activeRole);

      if (!allowedRoutes.includes(route)) return undefined;

      return buildPage(
        route,
        getTitleByRoute(t, route, menuItem.label),
        getIconByRoute(route),
        shouldUseExactMatch(route),
      );
    })
    .filter((page): page is SidebarItemsType => !!page);

  if (pages.length === 0) {
    return [
      {
        title: "",
        pages: getDefaultSection(t),
      },
    ];
  }

  return [
    {
      title: "",
      pages,
    },
  ];
};

const getNavItems = (t: TFunction) => [
  {
    title: "",
    pages: getDefaultSection(t),
  },
];

export default getNavItems;
